import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { COOLUM_BOOKING_URL } from '@/constants/urls';

interface Service {
  title: string;
  description: string;
  icon: string;
}

const ServicesSection: React.FC = () => {
  const treatments: Service[] = [
    {
      title: "Hormonal & Menstrual Health",
      description: "Balance hormones and alleviate menstrual symptoms with our targeted treatments.",
      icon: "🌿"
    },
    {
      title: "Pre-conception & Fertility",
      description: "Enhance fertility and prepare your body for conception with specialized care.",
      icon: "🍃"
    },
    {
      title: "Pregnancy Support",
      description: "Nurture your body and baby during pregnancy with gentle, effective treatments.",
      icon: "🌱"
    },
    {
      title: "Post-partum Care",
      description: "Restore balance and vitality after childbirth with dedicated post-partum support.",
      icon: "🌸"
    },
    {
      title: "Perimenopause & Menopause",
      description: "Navigate hormonal transitions with greater ease and fewer symptoms.",
      icon: "🍵"
    },
    {
      title: "General Health & Wellness",
      description: "Address pain, stress, sleep issues, and overall wellbeing with an integrated approach to care.",
      icon: "☯️"
    }
  ];

  const methods: Service[] = [
    {
      title: "Acupuncture",
      description: "Fine needles placed at specific points to restore energy flow and balance.",
      icon: "🧠"
    },
    {
      title: "Moxibustion",
      description: "Warming therapy that strengthens the blood, stimulates flow, and enhances healing.",
      icon: "🔥"
    },
    {
      title: "Cupping",
      description: "Creates suction to increase blood flow, release tension, and promote healing.",
      icon: "⭕"
    },
    {
      title: "Herbs & Supplements",
      description: "personalised herbal formulas to address your specific health concerns.",
      icon: "🌿"
    },
    {
      title: "Dietary Advice",
      description: "Nutrition guidance based on Chinese medicine principles for optimal health.",
      icon: "🥗"
    },
    {
      title: "Lifestyle Guidance",
      description: "Practical recommendations to enhance wellbeing in daily life.",
      icon: "✨"
    }
  ];

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
        <h2 className="mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Our Services</h2>
          <p className="max-w-3xl mx-auto text-lg text-gray-700">
            We customize your treatment plan to suit you and your body, addressing your specific health concerns with a comprehensive approach.
          </p>
        </div>
        
        <div className="mb-20">
          <h3 className="text-2xl font-serif font-medium mb-8 text-center text-sage-800">What We Treat</h3>
          <p className="text-center mt-8 text-gray-700 italic">
            We have a special interest in women's health, but we welcome general appointments for individuals of all genders and sexual orientations.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatments.map((service, index) => (
              <Card 
                key={index} 
                className="overflow-hidden rounded-2xl shadow-lg border-2 border-amber-200/30 card-hover"
              >
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h4 className="font-serif text-xl font-medium mb-2 text-sage-700">{service.title}</h4>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        <div>
          <h3 className="text-2xl font-serif font-medium mb-8 text-center text-sage-800">Treatment Methods</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methods.map((method, index) => (
              <Card 
                key={index} 
                className="overflow-hidden rounded-2xl shadow-lg border-2 border-amber-200/30 card-hover"
              >
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{method.icon}</div>
                  <h4 className="font-serif text-xl font-medium mb-2 text-sage-700">{method.title}</h4>
                  <p className="text-gray-600">{method.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
